import { algoliasearch } from "algoliasearch"
import type { SearchClient } from "algoliasearch"
import type { StateMapping, UiState } from "instantsearch.js"
import { history } from "instantsearch.js/es/lib/routers"
import type { BrowserHistoryArgs } from "instantsearch.js/es/lib/routers/history"
import { createInsightsMiddleware } from "instantsearch.js/es/middlewares"
import { createContext, useEffect, useLayoutEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  Configure,
  InstantSearch,
  useHitsPerPage,
  useInstantSearch,
  usePagination,
  useRefinementList,
  useSearchBox,
  useSortBy,
  useToggleRefinement,
} from "react-instantsearch-core"
import { resolvePath } from "react-router"
import searchInsights from "search-insights"

import { reactQueryCache } from "@sap/utils/algolia"

import queryClient from "@/api/queryClient"
import Router from "@/routes"
import { useAuthStore } from "@/store/auth"

type RouterState = {
  query?: string
  category?: string[]
  brandNew?: boolean
  page?: number
  sortBy?: string
  outdoor?: boolean
  indoor?: boolean
  stackable?: boolean
  sustainable?: boolean
  satellietOriginals?: boolean
}

interface RouterArgs extends BrowserHistoryArgs<UiState> {
  inPopState?: boolean
}

function stateMapping(indexName: keyof UiState): StateMapping<UiState, UiState[typeof indexName]> {
  return {
    stateToRoute(uiState): RouterState {
      const indexUiState = uiState[indexName] || {}
      //console.log("stateToRoute:indexUiState", indexUiState)

      const routerState = {
        query: indexUiState.query,
        category: indexUiState.refinementList?.category_plural,
        brandNew: indexUiState.toggle?.brand_new,
        outdoor: indexUiState.toggle?.outdoor,
        indoor: indexUiState.toggle?.indoor,
        stackable: indexUiState.toggle?.stackable,
        sustainable: indexUiState.toggle?.sustainable,
        satellietOriginals: indexUiState.toggle?.satelliet_originals,
        page: indexUiState.page,
        sortBy: indexUiState.sortBy,
      }
      //console.log("stateToRoute:routerState", routerState)
      return routerState
    },
    routeToState(routeState: RouterState = {}) {
      //console.log("routeToState:routeState", routeState)

      return {
        [indexName]: {
          query: routeState.query,
          refinementList: {
            category_plural: routeState.category,
          },
          toggle: {
            brand_new: routeState.brandNew,
            outdoor: routeState.outdoor,
            indoor: routeState.indoor,
            stackable: routeState.stackable,
            sustainable: routeState.sustainable,
            satelliet_originals: routeState.satellietOriginals,
          },
          page: routeState.page,
          sortBy: routeState.sortBy || indexName,
        },
      } as UiState
    },
  }
}

export default function AlgoliaProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()
  const { user } = useAuthStore((state) => state)

  const indexName = useMemo(() => {
    return `sap_pl_model_${i18n.language}_${import.meta.env.VITE_ALGOLIA_ENV}`
  }, [i18n.language])

  const routing = useMemo(() => {
    return {
      router: history({
        start(onUpdate) {
          Router.subscribe((e) => {
            if (e.location.pathname === "/") {
              onUpdate()
            }
          })
        },
        push(url) {
          const path = resolvePath(url)
          const location = Router.state.location.pathname
          if (location === "/") {
            path.pathname = location
            Router.navigate(path)
          }
        },
        getLocation() {
          this.inPopState = false
          return window.location
        },
        writeDelay: 200,
        cleanUrlOnDispose: false,
      } as RouterArgs),
      stateMapping: stateMapping(indexName) as StateMapping & UiState,
    }
  }, [indexName])

  const searchClient = useMemo(() => {
    const algoliaClient = algoliasearch(
      import.meta.env.VITE_ALGOLIA_APP_ID || "",
      user?.algolia_token || "",
      {
        responsesCache: reactQueryCache(queryClient),
      }
    )

    const searchClient = {
      ...algoliaClient,
      search(requests) {
        const shouldSearch = Router.state.location.pathname === "/"
        if (shouldSearch) {
          return algoliaClient.search(requests)
        }
        return Promise.resolve({
          results: Array.isArray(requests)
            ? requests.map(() => ({
                hits: [],
                nbHits: 0,
                nbPages: 0,
                page: 0,
                processingTimeMS: 0,
                hitsPerPage: 0,
                exhaustiveNbHits: false,
                query: "",
                params: "",
              }))
            : [],
        })
      },
      searchForFacetValues: algoliaClient.searchForFacetValues,
    } as SearchClient

    return searchClient
  }, [user?.algolia_token])

  return (
    <InstantSearch
      key={indexName}
      searchClient={searchClient}
      routing={routing}
      indexName={indexName}
      stalledSearchDelay={0}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <Configure index={indexName} />
      <UserInsightsConnector />
      <VirtualRefinementList />
      <VirtualToggleRefinement />
      <VirtualPagination />
      <VirtualHitsPerPage />
      <VirtualSearchBox />
      <VirtualSortBy>{children}</VirtualSortBy>
    </InstantSearch>
  )
}

function UserInsightsConnector() {
  const { user } = useAuthStore((state) => state)
  const { addMiddlewares } = useInstantSearch()

  useEffect(() => {}, [])

  useLayoutEffect(() => {
    if (user?.id && user?.algolia_token) {
      searchInsights("init", {
        appId: import.meta.env.VITE_ALGOLIA_APP_ID || "",
        apiKey: user.algolia_token,
      })
      searchInsights("setUserToken", `${user.id}`)

      const middleware = createInsightsMiddleware({
        insightsClient: searchInsights,
      })

      return addMiddlewares(middleware)
    }
  }, [addMiddlewares, user?.id, user?.algolia_token])

  return null
}

function VirtualRefinementList() {
  useRefinementList({
    attribute: "category_plural",
    sortBy: ["name:asc"],
    limit: 20,
  })

  return null
}

function VirtualToggleRefinement() {
  useToggleRefinement({
    attribute: "brand_new",
  })
  useToggleRefinement({
    attribute: "outdoor",
  })
  useToggleRefinement({
    attribute: "indoor",
  })
  useToggleRefinement({
    attribute: "stackable",
  })
  useToggleRefinement({
    attribute: "sustainable",
  })
  useToggleRefinement({
    attribute: "satelliet_originals",
  })
  return null
}

function VirtualPagination() {
  usePagination()

  return null
}

function VirtualHitsPerPage() {
  useHitsPerPage({
    items: [{ label: "16 hits per page", value: 16, default: true }],
  })
  return null
}

function VirtualSearchBox() {
  useSearchBox()

  return null
}

// eslint-disable-next-line react-refresh/only-export-components
export const SortByContext = createContext<{
  refine: (value: string) => void
  currentRefinement: string
  options: { label: string; value: string }[]
}>({
  refine: () => {},
  currentRefinement: "",
  options: [],
})

function VirtualSortBy({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation()

  const defaultIndexName = `sap_pl_model_${i18n.language}_${import.meta.env.VITE_ALGOLIA_ENV}`
  const descNameIndexName = `sap_pl_model_${i18n.language}_sort_name_desc_${
    import.meta.env.VITE_ALGOLIA_ENV
  }`

  const sortBy = useSortBy({
    items: [
      { label: t("ascName"), value: defaultIndexName },
      {
        label: t("descName"),
        value: descNameIndexName,
      },
    ],
  })

  useEffect(() => {
    if (!sortBy.options.some((option) => option.value === sortBy.currentRefinement)) {
      sortBy.refine(defaultIndexName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy.options])

  return <SortByContext.Provider value={sortBy}>{children}</SortByContext.Provider>
}
